import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, Button, FlatList, TouchableOpacity, Alert, Switch } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import DateTimePicker from '@react-native-community/datetimepicker';
import { Picker } from '@react-native-picker/picker';
import * as Notifications from 'expo-notifications';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { NavigationContainer } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';

// Request permissions for notifications
Notifications.requestPermissionsAsync();

const Tab = createBottomTabNavigator();

function HomeScreen({ navigation }) {
  const [tasks, setTasks] = useState([]);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [completedTasks, setCompletedTasks] = useState(0);

  const loadTasks = async () => {
    try {
      const storedTasks = await AsyncStorage.getItem('tasks');
      if (storedTasks) {
        const parsedTasks = JSON.parse(storedTasks);
        setTasks(parsedTasks);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    setPendingTasks(tasks.filter(task => !task.completed).length);
    setCompletedTasks(tasks.filter(task => task.completed).length);
  }, [tasks]);

  return (
    <View style={{ flex: 1, padding: 20, justifyContent: 'center', alignItems: 'center' }}>
      <Text style={{ fontSize: 24, fontWeight: 'bold', marginBottom: 10 }}>🏠 Welcome to My CRM</Text>
      <Text style={{ fontSize: 18, marginBottom: 7 }}>📌 Pending Tasks: {pendingTasks}</Text>
      <Text style={{ fontSize: 18, marginBottom: 20 }}>✅ Completed Tasks: {completedTasks}</Text>
      <View style={{ marginTop: 10, width: '80%' }}>
  <Button title="Refresh" onPress={loadTasks} />
</View>

<View style={{ marginTop: 10, width: '80%' }}>
  <Button title="Go to Tasks" onPress={() => navigation.navigate('Tasks')} />
</View>

    </View>
  );
  
}


function TaskScreen() {
  const [task, setTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, completed, pending
  const [priority, setPriority] = useState('Medium');
  const [dueDate, setDueDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  useEffect(() => {
    const loadTasks = async () => {
      try {
        const storedTasks = await AsyncStorage.getItem('tasks');
        if (storedTasks) setTasks(JSON.parse(storedTasks));
      } catch (error) {
        console.error('Error loading tasks:', error);
      }
    };
    loadTasks();
  }, []);

  useEffect(() => {
    AsyncStorage.setItem('tasks', JSON.stringify(tasks)).catch(error =>
      console.error('Error saving tasks:', error)
    );
  }, [tasks]);

  const scheduleNotification = async (task) => {
    await Notifications.scheduleNotificationAsync({
      content: {
        title: "Task Reminder ⏰",
        body: `Don't forget: ${task.text} (Due: ${task.dueDate})`,
      },
      trigger: { seconds: 10 }, // Customize when notifications should appear
    });
  };

  const addTask = () => {
    if (task.trim() === '') return;
    const newTask = {
      id: Date.now().toString(),
      text: task,
      completed: false,
      priority,
      dueDate: dueDate.toDateString(),
    };
    setTasks([...tasks, newTask]);
    setTask('');
    scheduleNotification(newTask);
  };

  const editTask = () => {
    setTasks(tasks.map(t => (t.id === editingTask.id ? { ...t, text: task, priority, dueDate: dueDate.toDateString() } : t)));
    setTask('');
    setEditingTask(null);
  };

  const toggleComplete = (id) => {
    setTasks(tasks.map(t => (t.id === id ? { ...t, completed: !t.completed } : t)));
  };

  const deleteTask = (id) => {
    Alert.alert('Delete Task', 'Are you sure?', [
      { text: 'Cancel' },
      { text: 'Delete', onPress: () => setTasks(tasks.filter(t => t.id !== id)) }
    ]);
  };

  const filteredTasks = tasks.filter(t =>
    (filter === 'all' || (filter === 'completed' && t.completed) || (filter === 'pending' && !t.completed)) &&
    t.text.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <View style={{ flex: 1, padding: 20, backgroundColor: isDarkMode ? '#343a40' : '#f8f9fa' }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
        <Text style={{ fontSize: 24, fontWeight: 'bold', color: isDarkMode ? 'white' : 'black' }}>📋 My CRM Tasks</Text>
        <Switch value={isDarkMode} onValueChange={setIsDarkMode} />
      </View>

      <TextInput
        value={task}
        onChangeText={setTask}
        placeholder="Enter task..."
        style={{
          borderWidth: 1, padding: 10, marginBottom: 10, borderRadius: 5,
          backgroundColor: 'white', color: 'black'
        }}
      />

      <Picker selectedValue={priority} onValueChange={setPriority} style={{ marginBottom: 10 }}>
        <Picker.Item label="High" value="High" />
        <Picker.Item label="Medium" value="Medium" />
        <Picker.Item label="Low" value="Low" />
      </Picker>

      <TouchableOpacity onPress={() => setShowDatePicker(true)} style={{ marginBottom: 10 }}>
        <Text style={{ fontSize: 16, color: 'blue' }}>📅 Set Due Date: {dueDate.toDateString()}</Text>
      </TouchableOpacity>

      {showDatePicker && (
        <DateTimePicker
          value={dueDate}
          mode="date"
          display="default"
          onChange={(event, selectedDate) => {
            setShowDatePicker(false);
            if (selectedDate) setDueDate(selectedDate);
          }}
        />
      )}

      {editingTask ? (
        <Button title="Update Task" onPress={editTask} />
      ) : (
        <Button title="Add Task" onPress={addTask} />
      )}

      <TextInput
        placeholder="Search tasks..."
        value={search}
        onChangeText={setSearch}
        style={{
          borderWidth: 1, padding: 10, marginTop: 10, borderRadius: 5,
          backgroundColor: 'white', color: 'black'
        }}
      />

      <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginVertical: 10 }}>
        <Button title="All" onPress={() => setFilter('all')} />
        <Button title="Pending" onPress={() => setFilter('pending')} />
        <Button title="Completed" onPress={() => setFilter('completed')} />
      </View>

      <FlatList
        data={filteredTasks}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View style={{
            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
            padding: 10, marginTop: 10, backgroundColor: item.completed ? '#d4edda' : 'white',
            borderRadius: 5, borderLeftWidth: 5, borderLeftColor: item.priority === 'High' ? 'red' : item.priority === 'Medium' ? 'orange' : 'green'
          }}>
            <TouchableOpacity onPress={() => toggleComplete(item.id)} style={{ flex: 1 }}>
              <Text style={{
                fontSize: 18, textDecorationLine: item.completed ? 'line-through' : 'none',
                color: 'black'
              }}>
                {item.text} (📅 {item.dueDate})
              </Text>
            </TouchableOpacity>

            <Button title="✏️" onPress={() => { setEditingTask(item); setTask(item.text); setPriority(item.priority); setDueDate(new Date(item.dueDate)); }} />
            <Button title="❌" onPress={() => deleteTask(item.id)} color="red" />
          </View>
        )}
      />
    </View>
  );
}
export default function App() {
  return (
    <NavigationContainer>
      <Tab.Navigator
        screenOptions={({ route }) => ({
          headerShown: false,
          tabBarIcon: ({ color, size }) => {
            let iconName;

            if (route.name === 'Home') {
              iconName = 'home'; // Home Icon
            } else if (route.name === 'Tasks') {
              iconName = 'list'; // Tasks Icon
            } else if (route.name === 'Profile') {
              iconName = 'person'; // Profile Icon
            }

            return <Ionicons name={iconName} size={size} color={color} />;
          },
          tabBarActiveTintColor: '#007bff', // Color when active
          tabBarInactiveTintColor: 'gray', // Color when inactive
          tabBarStyle: { backgroundColor: '#f8f9fa', paddingBottom: 5 }, // Style for tab bar
        })}
      >
        <Tab.Screen name="Home" component={HomeScreen} />
        <Tab.Screen name="Tasks" component={TaskScreen} />
        <Tab.Screen
          name="Profile"
          component={() => (
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
              <Text style={{ fontSize: 20 }}>👤 Profile Screen</Text>
            </View>
          )}
        />
      </Tab.Navigator>
    </NavigationContainer>
  );
}
// ✅ Function for Profile Screen inside App.js
function ProfileScreen() {
  return (
    <View style={styles.container}>
      <Image 
        source={{ uri: 'https://via.placeholder.com/100' }} 
        style={styles.profileImage} 
      />
      <Text style={styles.name}>John Doe</Text>
      <Text style={styles.email}>john@example.com</Text>

      <TouchableOpacity style={styles.button}>
        <Text style={styles.buttonText}>✏️ Edit Profile</Text>
      </TouchableOpacity>
      
      <TouchableOpacity style={[styles.button, styles.logoutButton]}>
        <Text style={styles.buttonText}>🚪 Logout</Text>
      </TouchableOpacity>
    </View>
  );
}

